import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CalendarCheck,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileSignature,
  Gavel,
  HelpCircle,
  Landmark,
  MapPin,
  Megaphone,
  Plus,
  ScrollText,
  ThumbsUp,
  Users as UsersIcon,
} from "lucide-react";
import { Avatar, Button, EmptyState, Pill } from "../ui";
import {
  governanceApi,
  errMessage,
  formatDateTime,
  ISSUE_KIND_META,
  ISSUE_STATUS_META,
  MEETING_KIND_META,
  MEETING_STATUS_META,
} from "./api";
import NewIssueDialog from "./NewIssueDialog";
import CreateMeetingDialog, { meetingKindsFor } from "./CreateMeetingDialog";
import "./GovernancePage.css";

const PAGE_SIZE = 12;

const TABS = [
  { id: "home", label: "Home" },
  { id: "issues", label: "Issues" },
  { id: "meetings", label: "Meetings" },
  { id: "decisions", label: "Decisions" },
];

const TAB_SUBTITLES = {
  issues:
    "Raise a Topic to gather support, or ask the board a Query. This is where ideas begin.",
  meetings:
    "General Body Meetings. RSVP, read the agenda and cast your vote on motions here.",
  decisions:
    "The permanent record of every motion the community has voted on — passed and rejected.",
};

export default function GovernancePage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState("");
  const [tab, setTab] = useState("home");
  const [newIssueOpen, setNewIssueOpen] = useState(false);
  const [createMeetingOpen, setCreateMeetingOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const bumpReload = useCallback(() => setReloadKey((k) => k + 1), []);

  const loadOverview = useCallback(() => {
    governanceApi
      .overview()
      .then((d) => {
        setOverview(d);
        setOverviewError("");
      })
      .catch((e) => setOverviewError(errMessage(e)));
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview, reloadKey]);

  const me = overview?.me || {};
  const attention = overview?.board_attention || [];
  const meetingKinds = meetingKindsFor(me);
  const canCreateMeeting = meetingKinds.length > 0;

  return (
    <div className="vsgov-shell">
      <div className="vsgov-head">
        <div>
          <h1>
            <Landmark size={22} /> Governance
          </h1>
          <p className="vsgov-sub">
            Your community's decision-making, in one place — raise an idea,
            gather support, meet and vote.
          </p>
        </div>
        <div className="vsgov-head-actions">
          {canCreateMeeting && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<CalendarPlus size={14} />}
              onClick={() => setCreateMeetingOpen(true)}
            >
              Create meeting
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => setNewIssueOpen(true)}
          >
            Raise an issue
          </Button>
        </div>
      </div>

      {overviewError && <div className="vsgov-error">{overviewError}</div>}

      {me.is_board && attention.length > 0 && (
        <div className="vsgov-attention" role="alert">
          <AlertTriangle size={18} />
          <div className="vsgov-attention-body">
            <strong>Board action required — GBM must be convened</strong>
            {attention.map((a) => (
              <Link
                key={a.issue_id}
                to={`/governance/issues/${a.issue_id}`}
                className="vsgov-attention-item"
              >
                {a.days_left < 0 ? (
                  <span className="vsgov-attention-overdue">OVERDUE</span>
                ) : (
                  <span className="vsgov-attention-days">
                    GBM required within {a.days_left}{" "}
                    {a.days_left === 1 ? "day" : "days"}
                  </span>
                )}
                <span className="vsgov-attention-title">{a.title}</span>
                <span className="vsgov-attention-meta">
                  {a.support_count} supports · deadline{" "}
                  {formatDateTime(a.deadline_at)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="vsgov-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`vsgov-tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {TAB_SUBTITLES[tab] && (
        <p className="vsgov-tab-sub">{TAB_SUBTITLES[tab]}</p>
      )}

      {tab === "home" && (
        <HomeTab
          overview={overview}
          reloadKey={reloadKey}
          bumpReload={bumpReload}
          onRaise={() => setNewIssueOpen(true)}
          goTab={setTab}
        />
      )}
      {tab === "issues" && (
        <IssuesTab reloadKey={reloadKey} onRaise={() => setNewIssueOpen(true)} />
      )}
      {tab === "meetings" && (
        <MeetingsTab
          reloadKey={reloadKey}
          canCreate={canCreateMeeting}
          onCreate={() => setCreateMeetingOpen(true)}
        />
      )}
      {tab === "decisions" && <ResolutionsTab reloadKey={reloadKey} />}

      <NewIssueDialog
        open={newIssueOpen}
        onOpenChange={setNewIssueOpen}
        onCreated={(issue) => {
          bumpReload();
          if (issue?.id) navigate(`/governance/issues/${issue.id}`);
        }}
      />
      <CreateMeetingDialog
        open={createMeetingOpen}
        onOpenChange={setCreateMeetingOpen}
        kinds={meetingKinds}
        onCreated={(meeting) => {
          bumpReload();
          if (meeting?.id) navigate(`/governance/meetings/${meeting.id}`);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Home — "Needs you" action centre + how-it-works guide + quick stats
// ---------------------------------------------------------------------------

function HomeTab({ overview, reloadKey, bumpReload, onRaise, goTab }) {
  const [agenda, setAgenda] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => {
    governanceApi
      .myAgenda()
      .then(setAgenda)
      .catch(() =>
        setAgenda({
          is_member: false,
          support: [],
          vote: [],
          polls: [],
          rsvp: [],
          counts: { total: 0 },
        })
      );
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const support = async (id) => {
    setBusyId(id);
    try {
      await governanceApi.supportIssue(id);
      bumpReload();
    } catch (e) {
      /* surfaced on the issue page */
    } finally {
      setBusyId("");
    }
  };
  const rsvp = async (id, response) => {
    setBusyId(id);
    try {
      await governanceApi.rsvp(id, response);
      bumpReload();
    } catch (e) {
      /* no-op */
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="vsgov-home">
      <NeedsYou
        agenda={agenda}
        busyId={busyId}
        onSupport={support}
        onRsvp={rsvp}
        onRaise={onRaise}
        goTab={goTab}
      />
      <HowItWorks />
      {overview && <QuickStats overview={overview} />}
    </div>
  );
}

const RSVP_AGENDA_SECTIONS = [
  {
    key: "rsvp",
    icon: CalendarCheck,
    title: "Reply to a meeting",
    blurb: "The board needs to know if you'll attend.",
  },
  {
    key: "vote",
    icon: Gavel,
    title: "Cast your vote",
    blurb: "Motions are open for your binding For / Against vote.",
  },
  {
    key: "polls",
    icon: BarChart3,
    title: "Share your view",
    blurb: "Quick, non-binding polls want your opinion.",
  },
  {
    key: "support",
    icon: ThumbsUp,
    title: "Add your support",
    blurb: "Topics that need 10% support to reach a meeting.",
  },
];

function NeedsYou({ agenda, busyId, onSupport, onRsvp, onRaise, goTab }) {
  if (!agenda) {
    return <div className="vsgov-loading">Loading what needs you…</div>;
  }
  if (!agenda.is_member) {
    return (
      <section className="vsgov-needs">
        <h2 className="vsgov-needs-head">
          <CheckCircle2 size={18} /> Welcome to Governance
        </h2>
        <div className="vsgov-needs-empty">
          You can read every issue, meeting and decision here. Giving support
          and voting are open to approved primary owners — once your villa
          ownership is approved, your to-dos will appear on this page.
        </div>
      </section>
    );
  }

  const total = agenda.counts?.total || 0;
  if (total === 0) {
    return (
      <section className="vsgov-needs">
        <h2 className="vsgov-needs-head">
          <CheckCircle2 size={18} /> You're all caught up
        </h2>
        <div className="vsgov-needs-empty">
          Nothing needs your attention right now. When a topic needs support, a
          motion opens for voting, or a meeting is called, it will show up here.
          <div className="vsgov-needs-empty-cta">
            <Button variant="secondary" size="sm" onClick={onRaise}>
              Raise an issue
            </Button>
            <Button variant="ghost" size="sm" onClick={() => goTab("meetings")}>
              See meetings
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="vsgov-needs">
      <h2 className="vsgov-needs-head">
        <span className="vsgov-needs-badge">{total}</span> Needs you
      </h2>
      <div className="vsgov-needs-groups">
        {RSVP_AGENDA_SECTIONS.map((sec) => {
          const items = agenda[sec.key] || [];
          if (!items.length) return null;
          const Icon = sec.icon;
          return (
            <div key={sec.key} className="vsgov-needs-group">
              <div className="vsgov-needs-group-head">
                <Icon size={16} />
                <span className="vsgov-needs-group-title">{sec.title}</span>
                <span className="vsgov-needs-group-count">{items.length}</span>
              </div>
              <p className="vsgov-needs-group-blurb">{sec.blurb}</p>
              <div className="vsgov-needs-items">
                {items.map((it) => (
                  <NeedsItem
                    key={
                      it.id || it.resolution_id || it.poll_id || it.meeting_id
                    }
                    section={sec.key}
                    item={it}
                    busy={
                      busyId ===
                      (it.id || it.meeting_id || "")
                    }
                    onSupport={onSupport}
                    onRsvp={onRsvp}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function NeedsItem({ section, item, busy, onSupport, onRsvp }) {
  if (section === "support") {
    return (
      <div className="vsgov-needs-item">
        <Link to={`/governance/issues/${item.id}`} className="vsgov-needs-link">
          {item.title}
          <span className="vsgov-needs-sub">
            {item.support_count}/{item.support_needed} supports
          </span>
        </Link>
        <Button
          variant="primary"
          size="sm"
          loading={busy}
          onClick={() => onSupport(item.id)}
        >
          Support
        </Button>
      </div>
    );
  }
  if (section === "rsvp") {
    return (
      <div className="vsgov-needs-item">
        <Link
          to={`/governance/meetings/${item.meeting_id}`}
          className="vsgov-needs-link"
        >
          {item.title}
          <span className="vsgov-needs-sub">
            {item.stale ? "Rescheduled — please reconfirm · " : ""}
            {formatDateTime(item.scheduled_at)}
          </span>
        </Link>
        <div className="vsgov-needs-rsvp">
          <Button
            variant="secondary"
            size="sm"
            loading={busy}
            onClick={() => onRsvp(item.meeting_id, "YES")}
          >
            Yes
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => onRsvp(item.meeting_id, "NO")}
          >
            No
          </Button>
        </div>
      </div>
    );
  }
  // vote + polls → route to the meeting, where the proper UI lives
  const to = `/governance/meetings/${item.meeting?.id}`;
  return (
    <div className="vsgov-needs-item">
      <Link to={to} className="vsgov-needs-link">
        {item.title}
        <span className="vsgov-needs-sub">{item.meeting?.title}</span>
      </Link>
      <Link to={to} className="vsgov-needs-go">
        {section === "vote" ? "Vote" : "Answer"} <ArrowRight size={14} />
      </Link>
    </div>
  );
}

const HOW_STEPS = [
  {
    icon: Megaphone,
    title: "1 · Raise",
    body: "Raise a Topic to put something to the community, or a Query to ask the board a question.",
  },
  {
    icon: ThumbsUp,
    title: "2 · Support",
    body: "Neighbours back a Topic. At 10% of owners, the board must call a meeting within 30 days.",
  },
  {
    icon: UsersIcon,
    title: "3 · Meet",
    body: "A General Body Meeting (GBM) is called. The agenda and any motions are circulated to all.",
  },
  {
    icon: Gavel,
    title: "4 · Decide",
    body: "Members vote on each motion. More For than Against and it passes — recorded forever.",
  },
];

const HOW_TERMS = [
  {
    term: "Topic vs Query",
    body: "A Topic is something you want the community to act on — it gathers support to reach a meeting. A Query is a question for the board, which they answer in writing. No support needed.",
  },
  {
    term: "Motion vs Poll",
    body: "A Motion (resolution) is a formal proposal voted For / Against / Abstain at a meeting — it is binding. A Poll is a quick, informal show of hands to gauge opinion — it is not binding.",
  },
  {
    term: "Support",
    body: "Backing an issue so it gets onto a meeting's agenda. When 10% of owners support a Topic, a GBM must be held.",
  },
  {
    term: "When can I vote?",
    body: "Voting on a motion opens the moment the meeting's notice is sent out, and closes once the board records the result. You'll always see a clear 'Voting open' label.",
  },
];

const HELP_KEY = "vsgov_help_open";

function HowItWorks() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(HELP_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem(HELP_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <section className={`vsgov-how ${open ? "open" : ""}`}>
      <button type="button" className="vsgov-how-toggle" onClick={toggle}>
        <HelpCircle size={18} />
        <span>New here? How governance works</span>
        <ChevronDown size={18} className="vsgov-how-chev" />
      </button>
      {open && (
        <div className="vsgov-how-body">
          <div className="vsgov-how-steps">
            {HOW_STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="vsgov-how-step">
                  <div className="vsgov-how-step-icon">
                    <Icon size={20} />
                  </div>
                  <div className="vsgov-how-step-title">{s.title}</div>
                  <div className="vsgov-how-step-body">{s.body}</div>
                </div>
              );
            })}
          </div>
          <div className="vsgov-how-terms">
            {HOW_TERMS.map((t) => (
              <div key={t.term} className="vsgov-how-term">
                <div className="vsgov-how-term-name">{t.term}</div>
                <div className="vsgov-how-term-body">{t.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function QuickStats({ overview }) {
  return (
    <div className="vsgov-stats">
      <div className="vsgov-stat">
        <span className="vsgov-stat-value">{overview.eligible_members}</span>
        <span className="vsgov-stat-label">Voting members</span>
        <span className="vsgov-stat-hint">approved primary owners</span>
      </div>
      <div className="vsgov-stat">
        <span className="vsgov-stat-value">{overview.support_threshold}</span>
        <span className="vsgov-stat-label">Support threshold</span>
        <span className="vsgov-stat-hint">
          10% — forces a GBM in {overview.board_deadline_days} days
        </span>
      </div>
      <div className="vsgov-stat">
        <span className="vsgov-stat-value">{overview.open_issues}</span>
        <span className="vsgov-stat-label">Open issues</span>
        <span className="vsgov-stat-hint">awaiting action</span>
      </div>
      <div className="vsgov-stat">
        <span className="vsgov-stat-value">{overview.upcoming_meetings}</span>
        <span className="vsgov-stat-label">Upcoming meetings</span>
        <span className="vsgov-stat-hint">notice issued</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Issues tab
// ---------------------------------------------------------------------------

const ISSUE_KIND_FILTERS = [
  { value: "", label: "All kinds" },
  ...Object.entries(ISSUE_KIND_META).map(([value, m]) => ({
    value,
    label: m.label,
  })),
];
const ISSUE_STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  ...Object.entries(ISSUE_STATUS_META).map(([value, m]) => ({
    value,
    label: m.label,
  })),
];

function IssuesTab({ reloadKey, onRaise }) {
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const list = usePagedList(
    governanceApi.listIssues,
    { kind, status },
    reloadKey
  );
  const facets = list.data?.facets || {};

  const kindOptions = withCounts(ISSUE_KIND_FILTERS, facets.kind);
  const statusOptions = withCounts(ISSUE_STATUS_FILTERS, facets.status);

  return (
    <div className="vsgov-issues-layout">
      <aside className="vsgov-side">
        <FilterSection
          title="Categories"
          options={kindOptions}
          value={kind}
          onChange={setKind}
        />
        <FilterSection
          title="Statuses"
          options={statusOptions}
          value={status}
          onChange={setStatus}
        />
      </aside>

      <div className="vsgov-issues-main">
        <ListToolbar list={list} noun="issues" />
        {list.error && <div className="vsgov-error">{list.error}</div>}
        {list.loading && list.items.length === 0 && (
          <div className="vsgov-loading">Loading issues…</div>
        )}

        {!list.loading && list.items.length === 0 && !list.error && (
          <EmptyState
            icon={<Megaphone size={28} strokeWidth={1.4} />}
            title="No issues here"
            subtitle="Nothing matches these filters. Be the first to put something before the community."
            cta={
              <Button variant="primary" size="sm" onClick={onRaise}>
                Raise an issue
              </Button>
            }
          />
        )}

        {list.items.length > 0 && (
          <div className="vsgov-list">
            {list.items.map((i) => (
              <IssueCard key={i.id} issue={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function IssueCard({ issue }) {
  const kindMeta = ISSUE_KIND_META[issue.kind] || {
    label: issue.kind,
    pill: "neutral",
  };
  const statusMeta = ISSUE_STATUS_META[issue.status] || {
    label: issue.status,
    pill: "neutral",
  };
  const pct =
    issue.support_needed > 0
      ? Math.min(
          100,
          Math.round((issue.support_count / issue.support_needed) * 100)
        )
      : 0;
  const needsSupport =
    issue.status === "OPEN" &&
    !issue.my_support &&
    ["TOPIC", "MEETING_REQUEST"].includes(issue.kind);

  return (
    <Link to={`/governance/issues/${issue.id}`} className="vsgov-card">
      <div className="vsgov-card-pills">
        <Pill variant={kindMeta.pill}>{kindMeta.label}</Pill>
        <Pill variant={statusMeta.pill}>{statusMeta.label}</Pill>
        {issue.my_support && <Pill variant="success">You support this</Pill>}
        {needsSupport && <Pill variant="warning">Needs support</Pill>}
      </div>
      <h3 className="vsgov-card-title">{issue.title}</h3>
      <div className="vsgov-card-meta">
        <Avatar
          name={issue.raised_by?.name || "System"}
          imageUrl={issue.raised_by?.avatar_url}
          size={20}
        />
        <span>
          {issue.raised_by?.name || "System"} ·{" "}
          {formatDateTime(issue.created_at)}
        </span>
      </div>
      {issue.kind !== "QUERY" && (
        <div className="vsgov-supportrow">
          <div
            className="vsgov-progress"
            role="progressbar"
            aria-valuenow={issue.support_count}
            aria-valuemin={0}
            aria-valuemax={issue.support_needed}
          >
            <div
              className={`vsgov-progress-fill ${pct >= 100 ? "met" : ""}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="vsgov-supportrow-label">
            {issue.support_count}/{issue.support_needed} supports ({pct}%)
          </span>
        </div>
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Meetings tab
// ---------------------------------------------------------------------------

const MEETING_STATUS_FILTERS = [
  { value: "", label: "All" },
  ...Object.entries(MEETING_STATUS_META).map(([value, m]) => ({
    value,
    label: m.label,
  })),
];

function MeetingsTab({ reloadKey, canCreate, onCreate }) {
  const [status, setStatus] = useState("");
  const list = usePagedList(governanceApi.listMeetings, { status }, reloadKey);
  const facets = list.data?.facets || {};
  const statusOptions = withCounts(MEETING_STATUS_FILTERS, facets.status);

  return (
    <div className="vsgov-issues-layout">
      <aside className="vsgov-side">
        <FilterSection
          title="Status"
          options={statusOptions}
          value={status}
          onChange={setStatus}
        />
      </aside>

      <div className="vsgov-issues-main">
        <ListToolbar list={list} noun="meetings" />
        {list.error && <div className="vsgov-error">{list.error}</div>}
        {list.loading && list.items.length === 0 && (
          <div className="vsgov-loading">Loading meetings…</div>
        )}

        {!list.loading && list.items.length === 0 && !list.error && (
          <EmptyState
            icon={<CalendarDays size={28} strokeWidth={1.4} />}
            title="No meetings"
            subtitle={
              canCreate
                ? "Draft a meeting, build the agenda, then issue the notice to all members."
                : "When the board issues a meeting notice it will appear here."
            }
            cta={
              canCreate ? (
                <Button variant="primary" size="sm" onClick={onCreate}>
                  Create meeting
                </Button>
              ) : undefined
            }
          />
        )}

        {list.items.length > 0 && (
          <div className="vsgov-list">
            {list.items.map((m) => (
              <MeetingCard key={m.id} meeting={m} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function RsvpTallyBar({ rsvp }) {
  const eligible = rsvp?.eligible || 0;
  const yes = rsvp?.yes || 0;
  const no = rsvp?.no || 0;
  const awaiting = rsvp?.awaiting ?? Math.max(0, eligible - yes - no);
  const total = Math.max(1, yes + no + awaiting);
  return (
    <div className="vsgov-tally">
      <div className="vsgov-tallybar" aria-hidden="true">
        <div
          className="vsgov-tallybar-yes"
          style={{ width: `${(yes / total) * 100}%` }}
        />
        <div
          className="vsgov-tallybar-no"
          style={{ width: `${(no / total) * 100}%` }}
        />
      </div>
      <div className="vsgov-tally-legend">
        <span className="yes">{yes} yes</span>
        <span className="no">{no} no</span>
        <span className="awaiting">{awaiting} awaiting</span>
      </div>
    </div>
  );
}

function MeetingCard({ meeting }) {
  const kindMeta = MEETING_KIND_META[meeting.kind] || {
    label: meeting.kind,
    pill: "neutral",
  };
  const statusMeta = MEETING_STATUS_META[meeting.status] || {
    label: meeting.status,
    pill: "neutral",
  };
  const noticed = !!meeting.notice_issued_at;
  const open = ["NOTICE_ISSUED", "RESCHEDULED"].includes(meeting.status);
  const replyNeeded = open && !meeting.my_rsvp;

  return (
    <Link to={`/governance/meetings/${meeting.id}`} className="vsgov-card">
      <div className="vsgov-card-pills">
        <Pill variant={kindMeta.pill}>{kindMeta.label}</Pill>
        <Pill variant={statusMeta.pill}>{statusMeta.label}</Pill>
        {meeting.my_rsvp && (
          <Pill
            variant={meeting.my_rsvp.response === "YES" ? "success" : "danger"}
          >
            You said {meeting.my_rsvp.response === "YES" ? "Yes" : "No"}
            {meeting.my_rsvp.stale ? " · reconfirm" : ""}
          </Pill>
        )}
        {replyNeeded && <Pill variant="warning">Reply needed</Pill>}
      </div>
      <h3 className="vsgov-card-title">{meeting.title}</h3>
      <div className="vsgov-card-meta">
        <CalendarDays size={14} />
        <span>{formatDateTime(meeting.scheduled_at)}</span>
        {meeting.venue && (
          <>
            <MapPin size={14} />
            <span>{meeting.venue}</span>
          </>
        )}
      </div>
      {noticed && <RsvpTallyBar rsvp={meeting.rsvp} />}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Decisions (resolutions register) tab
// ---------------------------------------------------------------------------

const RESOLUTION_OUTCOME_FILTERS = [
  { value: "", label: "All decided" },
  { value: "PASSED", label: "Passed" },
  { value: "REJECTED", label: "Did not pass" },
];

function ResolutionsTab({ reloadKey }) {
  const [outcome, setOutcome] = useState("");
  const list = usePagedList(
    governanceApi.listResolutions,
    { status: outcome },
    reloadKey
  );
  const facets = list.data?.facets?.status || {};
  const decidedTotal = (facets.PASSED || 0) + (facets.REJECTED || 0);
  const outcomeOptions = RESOLUTION_OUTCOME_FILTERS.map((o) => ({
    ...o,
    count: o.value === "" ? decidedTotal : facets[o.value] || 0,
  }));

  return (
    <div className="vsgov-issues-layout">
      <aside className="vsgov-side">
        <FilterSection
          title="Outcome"
          options={outcomeOptions}
          value={outcome}
          onChange={setOutcome}
        />
      </aside>

      <div className="vsgov-issues-main">
        <p className="vsgov-register-blurb">
          <ScrollText size={15} /> The association's statute book — every motion
          the general body has decided, kept permanently.
        </p>
        <ListToolbar list={list} noun="decisions" />

        {list.error && <div className="vsgov-error">{list.error}</div>}
        {list.loading && list.items.length === 0 && (
          <div className="vsgov-loading">Loading register…</div>
        )}

        {!list.loading && list.items.length === 0 && !list.error && (
          <EmptyState
            icon={<FileSignature size={28} strokeWidth={1.4} />}
            title="Nothing here yet"
            subtitle="Motions voted on at meetings are recorded here permanently — both those that pass and those that don't."
          />
        )}

        {list.items.length > 0 && (
          <div className="vsgov-register">
            {list.items.map((r) => {
              const passed = r.status === "PASSED";
              return (
                <div key={r.id} className="vsgov-resolution">
                  <div className="vsgov-card-pills">
                    <Pill variant={passed ? "done" : "danger"}>
                      {passed ? "Passed" : "Did not pass"}
                    </Pill>
                    {r.meeting && (
                      <Link
                        to={`/governance/meetings/${r.meeting.id}`}
                        className="vsgov-resolution-meeting"
                      >
                        <UsersIcon size={12} />{" "}
                        {(MEETING_KIND_META[r.meeting.kind] || {}).label ||
                          r.meeting.kind}{" "}
                        · {r.meeting.title}
                      </Link>
                    )}
                  </div>
                  <h3 className="vsgov-card-title">{r.title}</h3>
                  <p className="vsgov-resolution-text">{r.text}</p>
                  <div className="vsgov-resolution-foot">
                    <span className="vsgov-votes">
                      <strong>{r.votes_for}</strong> for ·{" "}
                      <strong>{r.votes_against}</strong> against ·{" "}
                      <strong>{r.votes_abstain}</strong> abstained
                    </span>
                    <span>Decided {formatDateTime(r.decided_at)}</span>
                  </div>
                  {(r.voters || []).length > 0 && (
                    <div className="vsgov-reg-voters">
                      {[
                        ["FOR", "For", "for"],
                        ["AGAINST", "Against", "against"],
                        ["ABSTAIN", "Abstained", "abstain"],
                      ].map(([v, label, cls]) => {
                        const group = (r.voters || []).filter(
                          (u) => u.vote === v
                        );
                        if (group.length === 0) return null;
                        return (
                          <div key={v} className={`vsgov-reg-voter-group ${cls}`}>
                            <span className="vsgov-reg-voter-label">{label}</span>
                            {group.map((u) => (
                              <span key={u.id} className="vsgov-reg-voter-chip">
                                {u.name}
                              </span>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits — paged-list hook, toolbar, filter sidebar
// ---------------------------------------------------------------------------

/** Server-paginated + sortable list. `apiCall` is a stable governanceApi
 *  method; `filters` is a plain object of filter params; `reloadKey` forces a
 *  refetch. Resets to the first page whenever filters or sort change. */
function usePagedList(apiCall, filters, reloadKey) {
  const key = JSON.stringify(filters);
  const [order, setOrder] = useState("newest");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Filters or sort changed → jump back to the first page.
  useEffect(() => {
    setPage(0);
  }, [key, order]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiCall({
      ...JSON.parse(key),
      order,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError("");
      })
      .catch((e) => {
        if (!alive) return;
        setData({ items: [], total: 0 });
        setError(errMessage(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [apiCall, key, order, page, reloadKey]);

  return {
    items: data?.items || [],
    total: data?.total || 0,
    data,
    error,
    loading,
    order,
    setOrder,
    page,
    setPage,
  };
}

function ListToolbar({ list, noun }) {
  const { total, page, order, setOrder, setPage, loading } = list;
  const start = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const end = Math.min(total, (page + 1) * PAGE_SIZE);
  const hasPrev = page > 0;
  const hasNext = end < total;

  return (
    <div className="vsgov-toolbar">
      <div className="vsgov-sort" role="group" aria-label="Sort order">
        <span className="vsgov-sort-label">Sort</span>
        <button
          type="button"
          className={`vsgov-sort-btn ${order === "newest" ? "active" : ""}`}
          onClick={() => setOrder("newest")}
        >
          Newest
        </button>
        <button
          type="button"
          className={`vsgov-sort-btn ${order === "oldest" ? "active" : ""}`}
          onClick={() => setOrder("oldest")}
        >
          Oldest
        </button>
      </div>
      <div className="vsgov-pager">
        <span className="vsgov-pager-info">
          {total === 0 ? `No ${noun}` : `${start}–${end} of ${total} ${noun}`}
        </span>
        <button
          type="button"
          className="vsgov-pager-btn"
          disabled={!hasPrev || loading}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          type="button"
          className="vsgov-pager-btn"
          disabled={!hasNext || loading}
          onClick={() => setPage((p) => p + 1)}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

/** Merge facet counts onto filter options. The facet spans the whole set for
 *  that dimension, so the "all" badge is just the sum of every bucket. */
function withCounts(options, facet) {
  if (!facet) return options;
  const all = Object.values(facet).reduce((a, b) => a + b, 0);
  return options.map((o) => ({
    ...o,
    count: o.value === "" ? all : facet[o.value] || 0,
  }));
}

function FilterSection({ title, options, value, onChange }) {
  return (
    <div className="vsgov-side-section">
      <div className="vsgov-side-title">{title}</div>
      <div className="vsgov-side-items" role="group">
        {options.map((o) => (
          <button
            key={o.value || "all"}
            type="button"
            className={`vsgov-side-item ${value === o.value ? "active" : ""}`}
            onClick={() => onChange(o.value)}
          >
            <span className="vsgov-side-label">{o.label}</span>
            {typeof o.count === "number" && (
              <span className="vsgov-side-count">{o.count}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
